import React from "react";
import { Button } from "@/components/ui/button";
import { CircleHelp, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AndroidInstructions from "./AndroidInstructions";
import IOSInstructions from "./IOSInstructions";
import WindowsInstructions from "./WindowsInstructions";
import SmartTvInstructions from "./SmartTvInstructions";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const HowToStart = ({ howToOpen, setHowToOpen }) => {
  const { t } = useTranslation();
  const lastSelectedDevice = localStorage.getItem("selectedDevice");
  const [selectedDevice, setSelectedDevice] = useState(
    lastSelectedDevice || ""
  );

  useEffect(() => {
    localStorage.setItem("selectedDevice", selectedDevice);
  }, [selectedDevice]);

  return (
    <Sheet open={howToOpen} onOpenChange={setHowToOpen}>
      <SheetTrigger asChild>
        <Button
          variant="secondary"
          asChild
          className="rounded-full w-fit cursor-pointer"
          onClick={() => setHowToOpen(!howToOpen)}
        >
          <div>
            <CircleHelp />
            <div>{t("how_to_start")}</div>
          </div>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full lg:min-w-[512px] flex flex-col bg-[var(--ring-offset)] p-6 rounded-3xl rounded-bl-xl rounded-br-xl shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04)] border-l border-r border-b border-t border-[var(--button-background)] mt-16 lg:mt-2 lg:mr-2 mb-2 gap-6 overflow-y-scroll"
      >
        <div className="justify-between items-start inline-flex w-full">
          <div className="self-stretch flex-col justify-start items-start gap-2 flex">
            <div className="self-stretch text-neutral-50 text-xl font-bold leading-7">
              {t("how_to_start_playing")}
            </div>
            <div className="self-stretch text-[var(--muted-foreground)] text-sm font-normal leading-tight">
              {t("follow_the_steps")}
            </div>
          </div>
          <div
            onClick={() => setHowToOpen(!howToOpen)}
            className="bg-white/0 rounded-full justify-center items-center gap-2 flex cursor-pointer"
          >
            <X className="w-full h-full relative overflow-hidden text-neutral-50" />
          </div>
        </div>
        <div className="flex items-center gap-2 mr-4">
          <Select
            value={lastSelectedDevice}
            onValueChange={(value) => {
              setSelectedDevice(value);
              localStorage.setItem("selectedDevice", value);
              console.log("Selected device:", value);
            }}
          >
            <SelectTrigger className="w-[180px] text-neutral-50 border-neutral-50">
              <SelectValue placeholder="Select device" />
            </SelectTrigger>
            <SelectContent className="text-neutral-50">
              <SelectItem value="windows">Windows</SelectItem>
              <SelectItem value="macos">MacOS</SelectItem>
              <SelectItem value="android">Android (Phone or Tablet)</SelectItem>
              <SelectItem value="ios">iOS (iPhone or iPad)</SelectItem>
              <SelectItem value="smarttv">Smart TV & other devices</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className={cn(selectedDevice === "" ? "hidden" : "visible")}>
          <div className="self-stretch flex-col justify-start items-start gap-6 flex">
            {selectedDevice === "windows" && <WindowsInstructions />}
            {selectedDevice === "macos" && (
              <WindowsInstructions useMacUrl={true} />
            )}
            {selectedDevice === "android" && <AndroidInstructions />}
            {selectedDevice === "ios" && <IOSInstructions />}
            {selectedDevice === "smarttv" && <SmartTvInstructions />}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default HowToStart;
